import React, { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
	Box,
	Typography,
	TextField,
	Button,
	IconButton,
	FormControl,
	Select,
	MenuItem,
	Switch,
	Card,
	Fade,
	Avatar,
	Stepper,
	Step,
	StepLabel,
} from '@mui/material'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import DeleteIcon from '@mui/icons-material/Delete'
import SaveIcon from '@mui/icons-material/Save'
import CategoryIcon from '@mui/icons-material/Category'
import DescriptionIcon from '@mui/icons-material/Description'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import { apiBase } from '../../services/apiBase'
import { api } from '../../utils/api'
import UserLayout from '../../layouts/UserLayout'
import { compressImage } from '../../utils/helper'

function EditProduct() {
	const navigate = useNavigate()
	const location = useLocation()
	const productData = location.state?.product

	// Modern step-based form
	const [activeStep, setActiveStep] = useState(0)
	const [completedSteps, setCompletedSteps] = useState(new Set())
	const [isSubmitting, setIsSubmitting] = useState(false)

	const [formData, setFormData] = useState({
		name: productData?.name || '',
		nameEn: productData?.nameEn || '',
		nameFr: productData?.nameFr || '',
		nameKo: productData?.nameKo || '',
		nameCn: productData?.nameCn || '',
		price: productData?.price || 0,
		salePrice: productData?.salePrice || 0,
		description: productData?.description || '',
		descriptionEn: productData?.descriptionEn || '',
		descriptionFr: productData?.descriptionFr || '',
		descriptionKo: productData?.descriptionKo || '',
		descriptionCn: productData?.descriptionCn || '',
		weight: productData?.weight || 0,
		status: productData?.status,
		category_id: productData?.category_id || 1,
		image: productData?.image || null,
		on_sale: productData?.on_sale || 0,
	})
	const [newImageFile, setNewImageFile] = useState(null) // Track new image file
	const [imagePreview, setImagePreview] = useState(productData?.image || null)

	const steps = [
		{ label: '01 General', icon: <DescriptionIcon /> },
		{ label: '02 Pricing', icon: <AttachMoneyIcon /> },
		{ label: '03 Settings', icon: <CategoryIcon /> }
	]

	const handleNext = () => {
		setActiveStep((prevStep) => prevStep + 1)
		setCompletedSteps(prev => new Set([...prev, activeStep]))
	}

	const handleStepClick = (step) => {
		setActiveStep(step)
	}

	const isStepCompleted = (step) => {
		return completedSteps.has(step)
	}

	const canProceedToNext = () => {
		switch (activeStep) {
			case 0:
				return formData.name && formData.description
			case 1:
				return formData.price > 0
			case 2:
				return true // Settings step is optional
			default:
				return true
		}
	}

	const handleChange = (event) => {
		const { name, value } = event.target
		setFormData((prev) => ({
			...prev,
			[name]: value,
		}))
	}

	const handleImageChange = async (event) => {
		const file = event.target.files[0]
		if (file) {
			// Compress image before setting to state
			const compressedFile = await compressImage(file)
			setNewImageFile(compressedFile) // Store the new image file
			setFormData((prev) => ({
				...prev,
				image: compressedFile,
			}))
			setImagePreview(URL.createObjectURL(compressedFile))
		}
	}

	const handleDeleteImage = (e) => {
		e.preventDefault()
		e.stopPropagation()
		setNewImageFile(null)
		setFormData((prev) => ({
			...prev,
			image: null,
		}))
		setImagePreview(null)
	}

	const handleSwitchChange = (event) => {
		setFormData((prev) => ({
			...prev,
			status: event.target.checked,
		}))
	}

	const handleSubmit = async (e) => {
		e.preventDefault()
		setIsSubmitting(true)
		try {
			// Create FormData
			const formDataPayload = new FormData()

			// Add basic fields
			formDataPayload.append('_id', productData._id)
			formDataPayload.append('id', productData.id)
			formDataPayload.append('name', formData.name)
			formDataPayload.append('nameEn', formData.nameEn)
			formDataPayload.append('nameFr', formData.nameFr)
			formDataPayload.append('nameKo', formData.nameKo)
			formDataPayload.append('nameCn', formData.nameCn)
			formDataPayload.append('price', formData.price)
			formDataPayload.append('sale_price', formData.salePrice || 0)
			formDataPayload.append('description', formData.description || '')
			formDataPayload.append('descriptionEn', formData.descriptionEn || '')
			formDataPayload.append('descriptionFr', formData.descriptionFr || '')
			formDataPayload.append('descriptionKo', formData.descriptionKo || '')
			formDataPayload.append('descriptionCn', formData.descriptionCn || '')
			formDataPayload.append('weight', formData.weight || 0)
			formDataPayload.append('category_id', formData.category_id || 1)
			formDataPayload.append('on_sale', formData.on_sale || 0)
			formDataPayload.append('status', formData.status)

			// Handle image: use uploadImage for new files, image for existing URLs
			if (newImageFile) {
				// New image file uploaded
				formDataPayload.append('uploadImage', newImageFile)
			} else {
				// Keep existing image URL
				formDataPayload.append('image', productData?.image || '')
			}

			const response = await apiBase.put(api.ZALO_MINI_APP_EDIT_PRODUCT(productData.id), formDataPayload)
			if (response) {
				alert('Cập nhật sản phẩm thành công')
				navigate('/admin/zalo-mini-app/product')
			} else {
				alert('Có lỗi xảy ra khi cập nhật sản phẩm')
			}
			console.log('Product updated:', response)
		} catch (error) {
			console.error('Error updating product:', error)
			let errorMessage = 'Có lỗi xảy ra khi cập nhật sản phẩm.'

			if (error.message.includes('PayloadTooLargeError') || error.message.includes('request entity too large')) {
				errorMessage = 'Kích thước ảnh quá lớn. Vui lòng thử lại với ảnh nhỏ hơn!'
			} else {
				errorMessage += ` ${error?.message}`
			}

			alert(`${errorMessage}\nVui lòng thử lại!`)
		} finally {
			setIsSubmitting(false)
		}
	}

	return (
		<UserLayout>
			<Box sx={{ 
				minHeight: '100vh',
				background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
				position: 'relative',
				'&::before': {
					content: '""',
					position: 'absolute',
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23cbd5e1" fill-opacity="0.3"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
					opacity: 0.5,
				}
			}}>
				{/* Modal Container */}
				<Box sx={{ 
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					minHeight: '100vh',
					p: 3,
				}}>
					<Card 
						elevation={0}
						sx={{
							width: '100%',
							maxWidth: 800,
							borderRadius: 4,
							overflow: 'hidden',
							background: 'white',
							boxShadow: '0 20px 60px rgba(0,0,0,0.1)',
							border: '1px solid rgba(0,0,0,0.05)',
						}}
					>
						{/* Modal Header */}
						<Box sx={{ 
							p: 4,
							borderBottom: '1px solid #e2e8f0',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
						}}>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
								<Avatar sx={{ 
									bgcolor: '#3b82f6',
									width: 40,
									height: 40,
								}}>
									<ShoppingCartIcon />
								</Avatar>
								<Box>
									<Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
										Edit Product
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Update your product information.
									</Typography>
								</Box>
							</Box>
							<IconButton 
								onClick={() => navigate(-1)}
								sx={{
									color: '#64748b',
									'&:hover': { 
										bgcolor: '#f1f5f9',
									},
								}}
							>
								<ArrowBackIosNewIcon />
							</IconButton>
						</Box>

						{/* Stepper */}
						<Box sx={{ p: 4, pb: 2 }}>
							<Stepper 
								activeStep={activeStep} 
								alternativeLabel
								sx={{
									'& .MuiStepLabel-root': {
										cursor: 'pointer',
									},
									'& .MuiStepLabel-label': {
										fontWeight: 600,
										fontSize: '0.875rem',
									},
									'& .MuiStepLabel-label.Mui-active': {
										color: '#3b82f6',
									},
									'& .MuiStepLabel-label.Mui-completed': {
										color: '#10b981',
									},
									'& .MuiStepIcon-root': {
										fontSize: '1.5rem',
										color: '#cbd5e1',
									},
									'& .MuiStepIcon-root.Mui-active': {
										color: '#3b82f6',
									},
									'& .MuiStepIcon-root.Mui-completed': {
										color: '#10b981',
									},
								}}
							>
								<Step key="General" completed={isStepCompleted(0)}>
									<StepLabel onClick={() => handleStepClick(0)}>
										01 General
									</StepLabel>
								</Step>
								<Step key="Pricing" completed={isStepCompleted(1)}>
									<StepLabel onClick={() => handleStepClick(1)}>
										02 Pricing
									</StepLabel>
								</Step>
								<Step key="Settings" completed={isStepCompleted(2)}>
									<StepLabel onClick={() => handleStepClick(2)}>
										03 Settings
									</StepLabel>
								</Step>
							</Stepper>
						</Box>

						{/* Form Content */}
						<Box sx={{ p: 4, pt: 0 }}>
							{/* Step 1: General Information */}
							{activeStep === 0 && (
								<Fade in timeout={300}>
									<Box>
										{/* Name Field */}
										<Box sx={{ mb: 4 }}>
											<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#1e293b' }}>
												Tên Sản Phẩm (Tiếng Việt) *
											</Typography>
											<TextField
												fullWidth
												placeholder="Universal Design System"
												name='name'
												value={formData.name}
												onChange={handleChange}
												variant='outlined'
												size='medium'
												sx={{
													'& .MuiOutlinedInput-root': {
														borderRadius: 2,
														'&:hover fieldset': {
															borderColor: '#3b82f6',
														},
														'&.Mui-focused fieldset': {
															borderColor: '#3b82f6',
															borderWidth: 2,
														},
													},
												}}
											/>
											<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
												Give your product a short and clear name in Vietnamese.
											</Typography>
										</Box>

										{/* Name English Field */}
										<Box sx={{ mb: 4 }}>
											<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#1e293b' }}>
												Tên Sản Phẩm (Tiếng Anh)
											</Typography>
											<TextField
												fullWidth
												placeholder="Universal Design System"
												name='nameEn'
												value={formData.nameEn}
												onChange={handleChange}
												variant='outlined'
												size='medium'
												sx={{
													'& .MuiOutlinedInput-root': {
														borderRadius: 2,
														'&:hover fieldset': {
															borderColor: '#3b82f6',
														},
														'&.Mui-focused fieldset': {
															borderColor: '#3b82f6',
															borderWidth: 2,
														},
													},
												}}
											/>
											<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
												Give your product a short and clear name in English.
											</Typography>
										</Box>

										{/* Name French Field */}
										<Box sx={{ mb: 4 }}>
											<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#1e293b' }}>
												Tên Sản Phẩm (Tiếng Pháp)
											</Typography>
											<TextField
												fullWidth
												placeholder="Universal Design System"
												name='nameFr'
												value={formData.nameFr}
												onChange={handleChange}
												variant='outlined'
												size='medium'
												sx={{
													'& .MuiOutlinedInput-root': {
														borderRadius: 2,
														'&:hover fieldset': {
															borderColor: '#3b82f6',
														},
														'&.Mui-focused fieldset': {
															borderColor: '#3b82f6',
															borderWidth: 2,
														},
													},
												}}
											/>
											<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
												Give your product a short and clear name in French.
											</Typography>
										</Box>

										{/* Name Korean Field */}
										<Box sx={{ mb: 4 }}>
											<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#1e293b' }}>
												Tên Sản Phẩm (Tiếng Hàn)
											</Typography>
											<TextField
												fullWidth
												placeholder="Universal Design System"
												name='nameKo'
												value={formData.nameKo}
												onChange={handleChange}
												variant='outlined'
												size='medium'
												sx={{
													'& .MuiOutlinedInput-root': {
														borderRadius: 2,
														'&:hover fieldset': {
															borderColor: '#3b82f6',
														},
														'&.Mui-focused fieldset': {
															borderColor: '#3b82f6',
															borderWidth: 2,
														},
													},
												}}
											/>
											<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
												Give your product a short and clear name in Korean.
											</Typography>
										</Box>

										{/* Name Chinese Field */}
										<Box sx={{ mb: 4 }}>
											<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#1e293b' }}>
												Tên Sản Phẩm (Tiếng Trung)
											</Typography>
											<TextField
												fullWidth
												placeholder="Universal Design System"
												name='nameCn'
												value={formData.nameCn}
												onChange={handleChange}
												variant='outlined'
												size='medium'
												sx={{
													'& .MuiOutlinedInput-root': {
														borderRadius: 2,
														'&:hover fieldset': {
															borderColor: '#3b82f6',
														},
														'&.Mui-focused fieldset': {
															borderColor: '#3b82f6',
															borderWidth: 2,
														},
													},
												}}
											/>
											<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
												Give your product a short and clear name in Chinese.
											</Typography>
										</Box>

										{/* Description Field */}
										<Box sx={{ mb: 4 }}>
											<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#1e293b' }}>
												Mô Tả Sản Phẩm (Tiếng Việt)
											</Typography>
											<TextField
												fullWidth
												placeholder="Powerful Figma Design System for creating landing pages, websites and dashboards."
												name='description'
												multiline
												rows={3}
												value={formData.description}
												onChange={handleChange}
												variant='outlined'
												size='medium'
												sx={{
													'& .MuiOutlinedInput-root': {
														borderRadius: 2,
														'&:hover fieldset': {
															borderColor: '#3b82f6',
														},
														'&.Mui-focused fieldset': {
															borderColor: '#3b82f6',
															borderWidth: 2,
														},
													},
												}}
											/>
											<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
												Give your product a short and clear description in Vietnamese.
											</Typography>
										</Box>

										{/* Description English Field */}
										<Box sx={{ mb: 4 }}>
											<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#1e293b' }}>
												Mô Tả Sản Phẩm (Tiếng Anh)
											</Typography>
											<TextField
												fullWidth
												placeholder="Powerful Figma Design System for creating landing pages, websites and dashboards."
												name='descriptionEn'
												multiline
												rows={3}
												value={formData.descriptionEn}
												onChange={handleChange}
												variant='outlined'
												size='medium'
												sx={{
													'& .MuiOutlinedInput-root': {
														borderRadius: 2,
														'&:hover fieldset': {
															borderColor: '#3b82f6',
														},
														'&.Mui-focused fieldset': {
															borderColor: '#3b82f6',
															borderWidth: 2,
														},
													},
												}}
											/>
											<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
												Give your product a short and clear description in English.
											</Typography>
										</Box>

										{/* Description French Field */}
										<Box sx={{ mb: 4 }}>
											<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#1e293b' }}>
												Mô Tả Sản Phẩm (Tiếng Pháp)
											</Typography>
											<TextField
												fullWidth
												placeholder="Powerful Figma Design System for creating landing pages, websites and dashboards."
												name='descriptionFr'
												multiline
												rows={3}
												value={formData.descriptionFr}
												onChange={handleChange}
												variant='outlined'
												size='medium'
												sx={{
													'& .MuiOutlinedInput-root': {
														borderRadius: 2,
														'&:hover fieldset': {
															borderColor: '#3b82f6',
														},
														'&.Mui-focused fieldset': {
															borderColor: '#3b82f6',
															borderWidth: 2,
														},
													},
												}}
											/>
											<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
												Give your product a short and clear description in French.
											</Typography>
										</Box>

										{/* Description Korean Field */}
										<Box sx={{ mb: 4 }}>
											<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#1e293b' }}>
												Mô Tả Sản Phẩm (Tiếng Hàn)
											</Typography>
											<TextField
												fullWidth
												placeholder="Powerful Figma Design System for creating landing pages, websites and dashboards."
												name='descriptionKo'
												multiline
												rows={3}
												value={formData.descriptionKo}
												onChange={handleChange}
												variant='outlined'
												size='medium'
												sx={{
													'& .MuiOutlinedInput-root': {
														borderRadius: 2,
														'&:hover fieldset': {
															borderColor: '#3b82f6',
														},
														'&.Mui-focused fieldset': {
															borderColor: '#3b82f6',
															borderWidth: 2,
														},
													},
												}}
											/>
											<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
												Give your product a short and clear description in Korean.
											</Typography>
										</Box>

										{/* Description Chinese Field */}
										<Box sx={{ mb: 4 }}>
											<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#1e293b' }}>
												Mô Tả Sản Phẩm (Tiếng Trung)
											</Typography>
											<TextField
												fullWidth
												placeholder="Powerful Figma Design System for creating landing pages, websites and dashboards."
												name='descriptionCn'
												multiline
												rows={3}
												value={formData.descriptionCn}
												onChange={handleChange}
												variant='outlined'
												size='medium'
												sx={{
													'& .MuiOutlinedInput-root': {
														borderRadius: 2,
														'&:hover fieldset': {
															borderColor: '#3b82f6',
														},
														'&.Mui-focused fieldset': {
															borderColor: '#3b82f6',
															borderWidth: 2,
														},
													},
												}}
											/>
											<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
												Give your product a short and clear description in Chinese.
											</Typography>
										</Box>

										{/* Image Upload */}
										<Box>
											<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#1e293b' }}>
												Hình ảnh
											</Typography>
											<label htmlFor='image-upload'>
												<Box
													sx={{
														display: 'flex',
														flexDirection: 'column',
														alignItems: 'center',
														justifyContent: 'center',
														width: '100%',
														height: 200,
														border: '2px dashed #cbd5e1',
														borderRadius: 2,
														cursor: 'pointer',
														position: 'relative',
														overflow: 'hidden',
														bgcolor: '#f8fafc',
														transition: 'all 0.3s ease',
														'&:hover': {
															borderColor: '#3b82f6',
															bgcolor: '#f1f5f9',
														},
													}}
												>
													{imagePreview ? (
														<Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
															<img
																src={imagePreview}
																alt='Preview'
																style={{
																	width: '100%',
																	height: '100%',
																	objectFit: 'cover',
																}}
															/>
															<IconButton
																onClick={handleDeleteImage}
																size='small'
																sx={{
																	position: 'absolute',
																	top: 8,
																	right: 8,
																	bgcolor: 'rgba(239,68,68,0.9)',
																	color: 'white',
																	'&:hover': {
																		bgcolor: 'rgba(239,68,68,1)',
																	},
																}}>
																<DeleteIcon />
															</IconButton>
														</Box>
													) : (
														<>
															<CloudUploadIcon sx={{ fontSize: 48, color: '#64748b', mb: 2 }} />
															<Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
																Drop your images here, or
															</Typography>
															<Button
																variant="outlined"
																size="small"
																sx={{
																	borderColor: '#cbd5e1',
																	color: '#64748b',
																	'&:hover': {
																		borderColor: '#3b82f6',
																		color: '#3b82f6',
																	},
																}}
															>
																Click to browse
															</Button>
														</>
													)}
												</Box>
											</label>
											<input
												id='image-upload'
												type='file'
												accept='image/*'
												onChange={handleImageChange}
												style={{ display: 'none' }}
											/>
											<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
												Add up to 10 images to your product. Used to represent your product during checkout, in email, social sharing and more.
											</Typography>
										</Box>
									</Box>
								</Fade>
							)}

							{/* Step 2: Pricing */}
							{activeStep === 1 && (
								<Fade in timeout={300}>
									<Box>
										<Box sx={{ mb: 4 }}>
											<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#1e293b' }}>
												Price *
											</Typography>
											<TextField
												fullWidth
												placeholder="0"
												name='price'
												value={formData.price ? formData.price.toLocaleString('vi-VN') : ''}
												onChange={(e) => {
													const value = e.target.value.replace(/[^\d]/g, '')
													const numValue = parseInt(value) || 0
													setFormData(prev => ({
														...prev,
														price: numValue
													}))
												}}
												type='text'
												size='medium'
												InputProps={{
													startAdornment: (
														<Box sx={{ mr: 1, color: '#64748b' }}>
															<Typography variant="body2" sx={{ fontWeight: 600 }}>
																₫
															</Typography>
														</Box>
													),
												}}
												inputProps={{
													inputMode: 'numeric',
													pattern: '[0-9]*',
												}}
												variant='outlined'
												sx={{
													'& .MuiOutlinedInput-root': {
														borderRadius: 2,
														'&:hover fieldset': {
															borderColor: '#3b82f6',
														},
														'&.Mui-focused fieldset': {
															borderColor: '#3b82f6',
															borderWidth: 2,
														},
													},
												}}
											/>
											<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
												Set the price for your product in Vietnamese Dong (VND).
											</Typography>
										</Box>

										<Box sx={{ mb: 4 }}>
											<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#1e293b' }}>
												Sale Price
											</Typography>
											<TextField
												fullWidth
												placeholder="0"
												name='salePrice'
												value={formData.salePrice ? formData.salePrice.toLocaleString('vi-VN') : ''}
												onChange={(e) => {
													const value = e.target.value.replace(/[^\d]/g, '')
													const numValue = parseInt(value) || 0
													setFormData(prev => ({
														...prev,
														salePrice: numValue
													}))
												}}
												type='text'
												size='medium'
												InputProps={{
													startAdornment: (
														<Box sx={{ mr: 1, color: '#ef4444' }}>
															<Typography variant="body2" sx={{ fontWeight: 600 }}>
																₫
															</Typography>
														</Box>
													),
												}}
												inputProps={{
													inputMode: 'numeric',
													pattern: '[0-9]*',
												}}
												variant='outlined'
												sx={{
													'& .MuiOutlinedInput-root': {
														borderRadius: 2,
														'&:hover fieldset': {
															borderColor: '#3b82f6',
														},
														'&.Mui-focused fieldset': {
															borderColor: '#3b82f6',
															borderWidth: 2,
														},
													},
												}}
											/>
											<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
												Set a sale price to offer discounts (optional).
											</Typography>
										</Box>
									</Box>
								</Fade>
							)}

							{/* Step 3: Settings */}
							{activeStep === 2 && (
								<Fade in timeout={300}>
									<Box>
										<Box sx={{ mb: 4 }}>
											<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#1e293b' }}>
												Category
											</Typography>
											<FormControl fullWidth size='medium'>
												<Select
													name='category_id'
													value={formData.category_id}
													onChange={handleChange}
													sx={{
														borderRadius: 2,
														'& .MuiOutlinedInput-notchedOutline': {
															borderColor: '#cbd5e1',
															'&:hover': {
																borderColor: '#3b82f6',
															},
														},
														'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
															borderColor: '#3b82f6',
															borderWidth: 2,
														},
													}}
												>
													<MenuItem value={1}>Nem nướng</MenuItem>
													<MenuItem value={2}>Nem sống</MenuItem>
													<MenuItem value={3}>Món ăn kèm</MenuItem>
													<MenuItem value={4}>Chả giò</MenuItem>
												</Select>
											</FormControl>
										</Box>

										<Box sx={{ mb: 4 }}>
											<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#1e293b' }}>
												Weight (kg)
											</Typography>
											<TextField
												fullWidth
												placeholder="0"
												name='weight'
												type='text'
												value={formData.weight ? formData.weight.toLocaleString('vi-VN') : ''}
												onChange={(e) => {
													const value = e.target.value.replace(/[^\d]/g, '')
													const numValue = parseInt(value) || 0
													setFormData(prev => ({
														...prev,
														weight: numValue
													}))
												}}
												size='medium'
												InputProps={{
													endAdornment: (
														<Typography variant='body2' color='text.secondary' sx={{ mr: 2 }}>
															kg
														</Typography>
													),
												}}
												variant='outlined'
												sx={{
													'& .MuiOutlinedInput-root': {
														borderRadius: 2,
														'&:hover fieldset': {
															borderColor: '#3b82f6',
														},
														'&.Mui-focused fieldset': {
															borderColor: '#3b82f6',
															borderWidth: 2,
														},
													},
												}}
											/>
											<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
												Set the weight of your product in kilograms (kg).
											</Typography>
										</Box>

										<Box sx={{ 
											display: 'flex', 
											alignItems: 'center', 
											justifyContent: 'space-between',
											p: 2,
											bgcolor: '#f8fafc',
											borderRadius: 2,
											border: '1px solid #e2e8f0',
										}}>
											<Box>
												<Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b' }}>
													Product Status
												</Typography>
												<Typography variant="body2" color="text.secondary">
													{formData.status ? 'Active' : 'Inactive'}
												</Typography>
											</Box>
											<Switch 
												checked={formData.status} 
												onChange={handleSwitchChange} 
												color='primary'
												size='medium'
											/>
										</Box>
									</Box>
								</Fade>
							)}
						</Box>

						{/* Footer Actions */}
						<Box sx={{ 
							p: 4,
							borderTop: '1px solid #e2e8f0',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
						}}>
							<Button
								variant="outlined"
								onClick={() => navigate(-1)}
								sx={{
									borderColor: '#cbd5e1',
									color: '#64748b',
									'&:hover': {
										borderColor: '#3b82f6',
										color: '#3b82f6',
									},
								}}
							>
								Cancel
							</Button>

							{activeStep < steps.length - 1 ? (
								<Button
									variant="contained"
									onClick={handleNext}
									disabled={!canProceedToNext()}
									endIcon={<ArrowBackIosNewIcon sx={{ transform: 'rotate(180deg)' }} />}
									sx={{
										bgcolor: '#3b82f6',
										'&:hover': {
											bgcolor: '#2563eb',
										},
										'&:disabled': {
											bgcolor: '#cbd5e1',
										},
									}}
								>
									Next step
								</Button>
							) : (
								<Button
									variant="contained"
									onClick={handleSubmit}
									disabled={isSubmitting || !canProceedToNext()}
									startIcon={isSubmitting ? null : <SaveIcon />}
									sx={{
										bgcolor: '#10b981',
										'&:hover': {
											bgcolor: '#059669',
										},
										'&:disabled': {
											bgcolor: '#cbd5e1',
										},
									}}
								>
									{isSubmitting ? 'Updating...' : 'Update Product'}
								</Button>
							)}
						</Box>
					</Card>
				</Box>
			</Box>
		</UserLayout>
	)
}

export default EditProduct
